import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment.development';
import { format, parseISO } from 'date-fns';
import { map, Observable } from 'rxjs';
import {
  iEmpHoursAddDto,
  iEmpHoursResult,
  iEmpHoursResultDto,
  iEmployeeShiftAddDto,
  iEmployeeShiftResult,
  iEmployeeShiftResultDto,
  ShiftResultDto,
} from 'src/app/helpers/Interfaces/shifts';
import {
  iEmployeeResult,
  iEmployeeResultDto,
} from 'src/app/helpers/Interfaces/employee';
import {
  iPaginationRequest,
  iPaginationResult,
} from 'src/app/helpers/Interfaces/pagination';
import { Shift } from 'src/app/helpers/Interfaces/shift';

@Injectable({
  providedIn: 'root',
})
export class ShiftsService {
  private _getAllShiftes = `${environment.base_url}/api/Shift/GetAll`;
  private _addShift = `${environment.base_url}/api/Shift/Add`;
  private _getAllWithRelationShip = `${environment.base_url}/api/Shift/GetAllNew`;
  private _updateShift = `${environment.base_url}/api/Shift/Update`;
  private _removeShift = `${environment.base_url}/api/Shift/Remove`;

  private _employeeShiftAddRange = `${environment.base_url}/api/EmployeeShift/AddRange`;
  private _employeeShiftGetByEmployee = `${environment.base_url}/api/EmployeeShift/GetByEmployeeId`;
  private _employeeShiftGetAllByEmployee = `${environment.base_url}/api/EmployeeShift/GetAllByEmployeeId`;

  private _empHoursAddRange = `${environment.base_url}/api/EmpHours/AddRange`;
  private _empHoursGetByEmployee = `${environment.base_url}/api/EmpHours/GetByEmployeeId`;
  private _employeeGetByShift = `${environment.base_url}/api/EmployeeShift/GetByShift`;

  constructor(private _http: HttpClient) { }

  transformShift(shift: any, payload?: any): any {
    const adjustTimezone = (time: string) => {
      const timeWithoutZ = time.replace(/[zZ]/g, '');
      const date = parseISO(timeWithoutZ);
      const timeZoneOffset = date.getTimezoneOffset() * 60000; // Convert to milliseconds
      return date.getTime() - timeZoneOffset;
    };

    const start = adjustTimezone(payload ? payload.startTime : shift.startTime);
    const end = adjustTimezone(payload ? payload.endTime : shift.endTime);

    return {
      ...shift,
      startTime: format(start, 'HH:mm'),
      endTime: format(end, 'HH:mm'),
      st: format(start, 'p'),
      et: format(end, 'hh:mm a'),
    };
  }

  getAllWithRelationShip(payload: iPaginationRequest) {
    return this._http
      .post<iPaginationResult<ShiftResultDto>>(
        `${this._getAllWithRelationShip}`,
        payload,
        {
          responseType: 'json',
        }
      )
      .pipe(
        map((res) => {
          res.data = res.data.map((x) => this.transformShift(x));
          return res;
        })
      );
  }
  GetAllShifts() {
    return this._http
      .get<any>(this._getAllShiftes, { responseType: 'json' })
      .pipe(map((shifts) => shifts.map((x: any) => this.transformShift(x))));
  }

  AddShift(payload: any) {
    return this._http
      .post<any>(this._addShift, payload, {
        responseType: 'json',
      })
      .pipe(map((x) => this.transformShift(x, payload)));
  }
  UpdateShift(payload: any) {
    return this._http
      .put<any>(this._updateShift, payload, {
        responseType: 'json',
      })
      .pipe(map((x) => this.transformShift(x, payload)));
  }
  DeleteShift(id: any) {
    return this._http.delete(`${this._removeShift}?id=${id}`);
  }
  GetTime() {
    return this._http
      .get<any>(`${environment.base_url}/api/Time`, {
        responseType: 'text',
      } as any)
      .pipe(
        map((x) => {
          return x;
        })
      );
  }
  employeeShiftAddRange(payload: iEmployeeShiftAddDto[]) {
    return this._http.post(`${this._employeeShiftAddRange}`, payload, {
      responseType: 'json',
    });
  }
  employeeShiftGetByEmployeeId(id: string) {
    return this._http.get<iEmployeeShiftResult[]>(
      `${this._employeeShiftGetByEmployee}?id=${id}`,
      {
        responseType: 'json',
      }
    );
  }
  employeeShiftGetAllByEmployeeId(id: string) {
    return this._http
      .get<iEmployeeShiftResult[]>(
        `${this._employeeShiftGetAllByEmployee}?id=${id}`,
        {
          responseType: 'json',
        }
      )
      .pipe(
        map((res) =>
          res.map(
            (r) =>
            ({
              ...r,
              sd: r.startDate ? format(r.startDate, 'yyyy-MM-dd') : '---',
              ed: r.endDate ? format(r.endDate, 'yyyy-MM-dd') : '---',
              cd: r.createdDate
                ? format(r.createdDate, 'yyyy-MM-dd p')
                : '---',
              shift: r.shift || { title: '---' },
              day: r.day ?? '---',
            } as iEmployeeShiftResultDto)
          )
        )
      );
  }
  empHoursAddRange(payload: iEmpHoursAddDto[]) {
    return this._http.post(`${this._empHoursAddRange}`, payload, {
      responseType: 'json',
    });
  }
  empHoursGetByEmployee(id: string): Observable<iEmpHoursResultDto[]>;
  empHoursGetByEmployee(id: string[]): Observable<iEmpHoursResultDto[]>;
  empHoursGetByEmployee(id: string | string[]) {
    const method = Array.isArray(id)
      ? this._http.post<iEmpHoursResult[]>(
        `${this._empHoursGetByEmployee}`,
        id,
        {
          responseType: 'json',
        }
      )
      : this._http.get<iEmpHoursResult[]>(
        `${this._empHoursGetByEmployee}?id=${id}`,
        {
          responseType: 'json',
        }
      );

    return method.pipe(
      map((res) =>
        res.map(
          (r) =>
          ({
            ...r,
            sd: r.startDate ? format(r.startDate, 'yyyy-MM-dd') : '---',
            ed: r.endDate ? format(r.endDate, 'yyyy-MM-dd') : '---',
            cd: r.createdDate ? format(r.createdDate, 'yyyy-MM-dd p') : '---',
          } as iEmpHoursResultDto)
        )
      )
    );
  }
  employeeGetByShift(
    shift: string,
    isHourly: boolean = false,
    companyId?: string,
    stationId?: string,
    isStartup?: boolean
  ) {
    const params: { [key: string]: string | boolean } = {
      shiftId: shift,
      isHourly,
    };
    if (companyId) params['companyId'] = companyId;
    if (stationId) params['stationId'] = stationId;
    if (isStartup) params['isStartup'] = isStartup;

    return this._http
      .get<iPaginationResult<iEmployeeResult>>(`${this._employeeGetByShift}`, {
        responseType: 'json',
        params,
      })
      .pipe(
        map((res) => ({
          ...res,
          data: res.data.map((emp) => {
            const employee: iEmployeeResultDto = { ...emp };
            // employee.Type = RoleTypes[employee.type];
            employee.name = `${employee.firstName} ${employee.lastName}`;
            employee.Type = employee.type;
            if (!employee.reportsToProfile?.includes(environment.base_url)) {
              employee.reportsToProfile = `${environment.base_url}/${employee.reportsToProfile}`;
            }
            if (!employee.profilePic?.includes(environment.base_url)) {
              employee.profilePic = `${environment.base_url}/${employee.profilePic}`;
            }
            return employee;
          }),
        }))
      );
  }
}
